import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Plus, Edit, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageOfficersPage = () => {
  const navigate = useNavigate();
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    institution_name: 'Eptral.Hospital Healthcare Solutions',
    password: ''
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'Super Admin') {
      toast.error('Only Super Admin can manage officers');
      navigate('/crm');
      return;
    }
    fetchOfficers();
  }, [navigate]);

  const fetchOfficers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/officers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOfficers(response.data);
    } catch (error) {
      toast.error('Failed to fetch officers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (officer = null) => {
    if (officer) {
      setEditMode(true);
      setSelectedOfficer(officer);
      setFormData({
        full_name: officer.full_name,
        email: officer.email,
        phone_number: officer.phone_number,
        institution_name: officer.institution_name,
        password: ''
      });
    } else {
      setEditMode(false);
      setSelectedOfficer(null);
      setFormData({
        full_name: '',
        email: '',
        phone_number: '',
        institution_name: 'Eptral.Hospital Healthcare Solutions',
        password: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone_number) {
      toast.error('All fields are required');
      return;
    }

    if (!editMode && !formData.password) {
      toast.error('Password is required for new officers');
      return;
    }

    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editMode) {
        await axios.put(
          `${API}/crm/officers/${selectedOfficer.id}`,
          {
            full_name: formData.full_name,
            email: formData.email,
            phone_number: formData.phone_number
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Officer updated successfully');
      } else {
        await axios.post(
          `${API}/crm/officers`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Officer created successfully');
      }
      
      setDialogOpen(false);
      fetchOfficers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save officer');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = async (officerId) => {
    if (!window.confirm('Are you sure you want to deactivate this officer?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crm/officers/${officerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Officer deactivated successfully');
      fetchOfficers();
    } catch (error) {
      toast.error('Failed to deactivate officer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading officers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Manage CRM Officers</h1>
            <Button onClick={() => navigate('/crm')} variant="outline" className="border-[#0056B3] text-[#0056B3]">
              Back to CRM
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>CRM Officers</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-officer-btn"
                  onClick={() => handleOpenDialog()}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Officer
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editMode ? 'Edit Officer' : 'Add New Officer'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email"
                      disabled={editMode}
                    />
                  </div>
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <Label>Institution Name</Label>
                    <Input
                      value={formData.institution_name}
                      onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
                      placeholder="Enter institution"
                    />
                  </div>
                  {!editMode && (
                    <div>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter password"
                      />
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={formLoading} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                      {formLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                      ) : (
                        editMode ? 'Update' : 'Create'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Officers Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Institution</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {officers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No officers found
                    </TableCell>
                  </TableRow>
                ) : (
                  officers.map((officer) => (
                    <TableRow key={officer.id}>
                      <TableCell className="font-medium">{officer.full_name}</TableCell>
                      <TableCell>{officer.email}</TableCell>
                      <TableCell>{officer.phone_number}</TableCell>
                      <TableCell>{officer.institution_name}</TableCell>
                      <TableCell>
                        <Badge className={officer.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {officer.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(officer)}
                            data-tooltip="Edit Officer"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(officer.id)}
                            data-tooltip="Deactivate Officer"
                            disabled={!officer.is_active}
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManageOfficersPage;